import { useState, useEffect, useRef } from 'react';
import { Search, Filter, ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useTranslations } from '@/hooks/use-translations';

interface Author {
    id: string;
    name: string;
}

interface Genre {
    id: string;
    name: string;
}

interface SearchFilterProps {
    onSearch: (search: string) => void;
    onSort: (sort: string) => void;
    onAuthorFilter: (authorId: string | null) => void;
    onGenreFilter?: (genreId: string | null) => void;
    authors?: Author[];
    genres?: Genre[];
    initialSearch?: string;
    initialSort?: string;
    initialAuthorId?: string | null;
    initialGenreId?: string | null;
    placeholder?: string;
}

export function SearchFilter({
    onSearch,
    onSort,
    onAuthorFilter,
    onGenreFilter,
    authors = [],
    genres = [],
    initialSearch = '',
    initialSort = 'newest',
    initialAuthorId = null,
    initialGenreId = null,
    placeholder = 'Search books...'
}: SearchFilterProps) {
    const { t } = useTranslations();
    const [search, setSearch] = useState(initialSearch);
    const [sort, setSort] = useState(initialSort);
    const [authorId, setAuthorId] = useState<string | null>(initialAuthorId);
    const [genreId, setGenreId] = useState<string | null>(initialGenreId);
    const [showFilters, setShowFilters] = useState(false);
    const isInitialMount = useRef(true);

    // Debounce search - only trigger after 800ms of no typing
    useEffect(() => {
        // Skip only on initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const timer = setTimeout(() => {
            onSearch(search);
        }, 800);

        return () => clearTimeout(timer);
    }, [search]);

    const handleSortChange = (newSort: string) => {
        setSort(newSort);
        onSort(newSort);
    };

    const handleAuthorChange = (value: string) => {
        const newAuthorId = value === 'all' ? null : value;
        setAuthorId(newAuthorId);
        onAuthorFilter(newAuthorId);
    };

    const handleGenreChange = (value: string) => {
        const newGenreId = value === 'all' ? null : value;
        setGenreId(newGenreId);
        onGenreFilter?.(newGenreId);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch(search);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSearch(search);
        }
    };

    const sortOptions = [
        { value: 'newest', label: t('Newest First') },
        { value: 'oldest', label: t('Oldest First') },
        { value: 'title_asc', label: t('Title A-Z') },
        { value: 'title_desc', label: t('Title Z-A') },
        { value: 'author_asc', label: t('Author A-Z') },
        { value: 'author_desc', label: t('Author Z-A') },
        { value: 'rating_high', label: t('Highest Rated') },
        { value: 'rating_low', label: t('Lowest Rated') },
    ];

    const currentSortLabel = sortOptions.find(opt => opt.value === sort)?.label || t('Sort');

    // Check if any filters are active
    const hasActiveFilters = search || authorId || genreId || sort !== 'newest';

    // Filter components (reusable for both mobile and desktop)
    const FilterComponents = () => (
        <>
            {/* Genre Filter */}
            {genres.length > 0 && (
                <SearchableSelect
                    options={[
                        { value: "all", label: t("All Genres") },
                        ...genres.map((genre) => ({
                            value: genre.id,
                            label: genre.name
                        }))
                    ]}
                    value={genreId || "all"}
                    onValueChange={handleGenreChange}
                    placeholder={t("Filter by genre...")}
                    searchPlaceholder={t("Search genres...")}
                    emptyText={t("No genres found.")}
                    className="w-full lg:w-[180px]"
                />
            )}

            {/* Author Filter */}
            {authors.length > 0 && (
                <SearchableSelect
                    options={[
                        { value: "all", label: t("All Authors") },
                        ...authors.map((author) => ({
                            value: author.id,
                            label: author.name
                        }))
                    ]}
                    value={authorId || "all"}
                    onValueChange={handleAuthorChange}
                    placeholder={t("Filter by author...")}
                    searchPlaceholder={t("Search authors...")}
                    emptyText={t("No authors found.")}
                    className="w-full lg:w-[200px]"
                />
            )}

            {/* Sort Dropdown */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full lg:w-auto lg:min-w-[140px] justify-between">
                        <span className="mr-2">{currentSortLabel}</span>
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px]">
                    {sortOptions.map((option) => (
                        <DropdownMenuItem
                            key={option.value}
                            onClick={() => handleSortChange(option.value)}
                            className={sort === option.value ? 'bg-accent' : ''}
                        >
                            {option.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );

    return (
        <form onSubmit={handleSearchSubmit} className="w-full">
            {/* Mobile Layout */}
            <div className="lg:hidden space-y-3">
                {/* Search bar with filter toggle */}
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                            type="text"
                            placeholder={t(placeholder)}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="pl-10"
                        />
                    </div>
                    <Button
                        type="button"
                        variant={hasActiveFilters ? "default" : "outline"}
                        size="icon"
                        onClick={() => setShowFilters(!showFilters)}
                        className="relative"
                    >
                        {showFilters ? <X className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
                        {hasActiveFilters && !showFilters && (
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full" />
                        )}
                    </Button>
                </div>

                {/* Collapsible filters */}
                {showFilters && (
                    <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                        <FilterComponents />
                    </div>
                )}
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center lg:gap-3">
                <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                        type="text"
                        placeholder={t(placeholder)}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="pl-10"
                    />
                </div>
                <div className="flex gap-2">
                    <FilterComponents />
                </div>
            </div>
        </form>
    );
}